import React, { useEffect, useState } from 'react';
import { useAdminStore } from '../../../lib/store-admin';
import { Module } from '../../../lib/supabase';
import { ModuleEditor } from './ModuleEditor';
import { ModuleDocUploader } from './ModuleDocUploader';

export const ModuleManager: React.FC = () => {
    const { institutions, modules, isLoading, fetchInstitutions, fetchModules, deleteModule, selectedInstitution, setSelectedInstitution } = useAdminStore();
    const [selectedModule, setSelectedModule] = useState<Module | undefined>(undefined);
    const [view, setView] = useState<'list' | 'selection' | 'editor' | 'uploader'>('list');

    useEffect(() => {
        fetchInstitutions();
    }, [fetchInstitutions]);

    useEffect(() => {
        if (selectedInstitution) {
            fetchModules(selectedInstitution.id);
        }
    }, [selectedInstitution, fetchModules]);

    const handleBackToInstitutions = () => {
        setSelectedInstitution(null);
        setView('list');
    };

    const handleCreateModule = () => {
        setSelectedModule(undefined);
        setView('selection');
    };

    const handleEditModule = (mod: Module) => {
        setSelectedModule(mod);
        setView('editor');
    };

    const handleDeleteModule = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (window.confirm('Bu modülü silmek istediğinize emin misiniz?')) {
            await deleteModule(id);
            if (selectedInstitution) {
                fetchModules(selectedInstitution.id);
            }
        }
    };

    if (!selectedInstitution) {
        return (
            <div className="simulation-manager-view">
                <h1>Modül Kurum Seçimi</h1>
                <p>Modüllerini yönetmek için bir kurum seçin.</p>
                <div className="institutions-grid">
                    {institutions.map((inst) => (
                        <div
                            key={inst.id}
                            className={`institution-card ${inst.is_active ? '' : 'inactive'}`}
                            onClick={() => setSelectedInstitution(inst)}
                        >
                            <div className="logo-placeholder">
                                {inst.logo_url ? (
                                    <img src={inst.logo_url} alt={inst.name} />
                                ) : (
                                    <span className="material-symbols-outlined">apartment</span>
                                )}
                            </div>
                            <h3>{inst.name}</h3>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // View: Selection Modal (Manual vs Doc)
    if (view === 'selection') {
        return (
            <div className="module-selection-view" style={{ padding: '40px', textAlign: 'center' }}>
                <button className="btn-back" onClick={() => setView('list')} style={{ position: 'absolute', top: '24px', left: '24px' }}>
                    <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <h2>Yeni Modül Oluşturma Yöntemi</h2>
                <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '40px' }}>
                    <div
                        onClick={() => setView('editor')}
                        style={{
                            border: '1px solid #e5e7eb', borderRadius: '12px', padding: '32px', width: '240px', cursor: 'pointer',
                            background: 'white', transition: 'transform 0.2s', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#3b82f6', marginBottom: '16px' }}>edit_square</span>
                        <h3 style={{ marginBottom: '8px' }}>Manuel Oluştur</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>Tüm içerikleri, soruları ve simülasyonları kendiniz ekleyin.</p>
                    </div>

                    <div
                        onClick={() => setView('uploader')}
                        style={{
                            border: '1px solid #e5e7eb', borderRadius: '12px', padding: '32px', width: '240px', cursor: 'pointer',
                            background: 'white', transition: 'transform 0.2s', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#10b981', marginBottom: '16px' }}>upload_file</span>
                        <h3 style={{ marginBottom: '8px' }}>Dökümandan Oluştur</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>Eğitim materyali yükleyin, AI modülleri otomatik oluştursun.</p>
                    </div>
                </div>
            </div>
        );
    }

    // View: Editor (Manual)
    if (view === 'editor') {
        return (
            <ModuleEditor
                institutionId={selectedInstitution.id}
                module={selectedModule}
                onClose={() => {
                    setView('list');
                    fetchModules(selectedInstitution.id);
                }}
            />
        );
    }

    // View: Uploader (AI)
    if (view === 'uploader') {
        return (
            <div style={{ padding: '24px' }}>
                <ModuleDocUploader
                    institutionId={selectedInstitution.id}
                    onCancel={() => setView('list')}
                    onComplete={() => {
                        setView('list');
                        fetchModules(selectedInstitution.id);
                    }}
                />
            </div>
        );
    }

    // View: List
    return (
        <div className="simulation-list-view">
            <div className="action-bar js-sb">
                <div className="header-left">
                    <button className="btn-back" onClick={handleBackToInstitutions}>
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div>
                        <h1>{selectedInstitution.name} Modülleri</h1>
                        <p>Bu kurum için oluşturulmuş eğitim modülleri.</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={handleCreateModule}>
                    <span className="material-symbols-outlined">add</span>
                    Yeni Modül
                </button>
            </div>

            {isLoading && <div className="loading-spinner">Yükleniyor...</div>}

            <div className="simulations-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px', marginTop: '24px' }}>
                {modules.length === 0 && !isLoading && (
                    <div className="empty-state">Henüz modül oluşturulmamış.</div>
                )}

                {modules.map((mod) => (
                    <div
                        key={mod.id}
                        className="content-card simulation-card"
                        style={{ cursor: 'pointer', transition: 'transform 0.2s', borderLeft: '4px solid #10b981' }}
                        onClick={() => handleEditModule(mod)}
                    >
                        <h3>{mod.title}</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {mod.description || 'Açıklama yok'}
                        </p>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                            {mod.items.map((item, idx) => (
                                <span key={idx} style={{
                                    fontSize: '11px',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    background: item.type === 'quiz' ? '#dbeafe' : item.type === 'mini_sim' ? '#fef3c7' : '#d1fae5',
                                    color: item.type === 'quiz' ? '#1e40af' : item.type === 'mini_sim' ? '#92400e' : '#065f46',
                                    fontWeight: 500
                                }}>
                                    {item.type === 'quiz' ? 'Soru' : item.type === 'mini_sim' ? 'Mini Sim' : 'Ana Sim'}
                                </span>
                            ))}
                        </div>

                        <div className="card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>{mod.items.length} adım</span>
                            <button
                                onClick={(e) => handleDeleteModule(mod.id, e)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                            >
                                <span className="material-symbols-outlined">delete</span>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
